import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useModelingListQuery, useTenantUsageQuery } from '@/hook';
import {
  Info,
  VBaseInput,
  VButton,
  VFlex,
  VInput,
  VModal,
  VSelect,
  VStack,
  VSwitch,
  VTooltip,
  useFlag,
} from '@/package';
import type { FrameData, Modeling, Task } from '@/type';

type PreAnnotationModalProps = {
  enabledTrackSession?: boolean;
  usableTrackSession?: FrameData['usableTrackSession'];
  task: Task;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  onConfirm: (
    modelingID: Modeling['id'],
    useTrackSession: boolean,
  ) => Promise<any>;
};

const PreAnnotationModal = ({
  usableTrackSession,
  task,
  visible,
  enabledTrackSession = false,
  setVisible,
  onConfirm,
}: PreAnnotationModalProps) => {
  const { t } = useTranslation();
  const { data: modelingList } = useModelingListQuery({
    versionType: 'SupplierModel',
  });
  const { data: tenantUsageInfo } = useTenantUsageQuery();
  const { value: useTrackSession, toggle: toggleUseNewTrackSession } =
    useFlag(enabledTrackSession);
  const [selectedModel, setSelectedModel] = useState(modelingList?.[0]?.id);

  useEffect(() => {
    if (selectedModel != null || !modelingList?.length) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedModel(modelingList[0].id);
  }, [modelingList, selectedModel]);

  return (
    <VModal
      visible={visible}
      onUpdateVisible={setVisible}
      size="medium"
      footer={() => {
        return (
          <VFlex className="w-full" justify="end" gap={0.25}>
            <VButton
              outlined
              size="md"
              variant="secondary"
              onClick={() => setVisible(false)}
            >
              {t('preAnnotation.cancel.inkindle')}
            </VButton>
            <VButton
              disabled={!selectedModel}
              size="md"
              onClick={() => {
                if (selectedModel)
                  return onConfirm(selectedModel, useTrackSession);
                else
                  return Promise.reject(
                    new Error('[PreAnnotationModal] `selectedModel` is null'),
                  );
              }}
            >
              {t('preAnnotation.startAnnotation.interlaboratory')}
            </VButton>
          </VFlex>
        );
      }}
      title="AI-annotation"
    >
      <VStack className="w-full" gap={1}>
        <VStack gap={0.25} className="w-full">
          <div className="text-body-xl whitespace-pre-line">
            {t('preAnnotation.modalTitle.arthrotomy')}
          </div>
          <ul className="text-body-lg text-gray-600">
            <li className="relative pl-4 before:absolute before:left-0 before:text-gray-500 before:content-['•']">
              {t('preAnnotation.frameNotStarted.orgiacs')}
            </li>
            <li className="relative pl-4 before:absolute before:left-0 before:text-gray-500 before:content-['•']">
              {t('preAnnotation.frameFailed.epanody')}
            </li>
            <li className="relative pl-4 before:absolute before:left-0 before:text-gray-500 before:content-['•']">
              {t('preAnnotation.frameUncancel.crocodiloid')}
            </li>
          </ul>
        </VStack>

        <VStack gap={1} className="w-full">
          {enabledTrackSession && (
            <VStack gap={0.25} className="w-full">
              <VFlex items="center" justify="between">
                <VFlex gap={0.25} items="center">
                  <div className="text-body-md font-semibold">
                    {t('preAnnotation.trackIdSession.tineoidea')}
                  </div>
                  <VTooltip
                    content={t('preAnnotation.trackIdSessionTooltip.polypidom')}
                  >
                    <Info className="size-4 text-info-500" />
                  </VTooltip>
                </VFlex>
                <VSwitch
                  size="md"
                  checked={useTrackSession}
                  onChange={toggleUseNewTrackSession}
                />
              </VFlex>

              {usableTrackSession && (
                <ul className="text-body-md text-[#2A58F6]">
                  <li className="relative pl-4 before:absolute before:left-0 before:content-['•']">
                    {t('preAnnotation.duplicatedTrackIdSession0.swatcher')}
                  </li>
                  <li className="relative pl-4 before:absolute before:left-0 before:content-['•']">
                    {t('preAnnotation.duplicatedTrackIdSession1.hatikvah')}
                  </li>
                </ul>
              )}
            </VStack>
          )}

          <div className="text-body-md font-semibold">
            {t('preAnnotation.modalSettings.samotherium')}
          </div>

          <VFlex gap={0.5} wrap="wrap" className="grid grid-cols-2">
            {task.extendClass?.map(item => {
              return (
                <VInput
                  key={item.id}
                  label={`${item.alias || item.name} ${t('preAnnotation.confidence.vaccinatory')}`}
                  orientation="horizontal"
                  size="sm"
                  readOnly
                  defaultValue={task?.classConfidence?.confidence
                    ?.find(confidence => confidence.id === item.id)
                    ?.confidence?.toFixed(3)}
                  className="w-[calc(100%/3-5.35px)]"
                  fluid
                />
              );
            })}
          </VFlex>

          <div className="text-body-md font-semibold">
            {t('preAnnotation.distanceAndModel.colortype')}
          </div>

          <VFlex gap={0.25} className="w-full">
            <VInput
              label={`${t('preAnnotation.distance.rejuvenate')}`}
              readOnly
              defaultValue={task?.distance}
              size="sm"
              orientation="horizontal"
              fluid
            />

            <VSelect
              fluid
              orientation="horizontal"
              size="sm"
              fitContent
              label={t('preAnnotation.modelSelect.entablement')}
              required
              value={selectedModel}
              onChange={e => setSelectedModel(e.target.value)}
              itemList={
                modelingList?.map(v => ({
                  value: v.id,
                  label: v.name,
                })) ?? []
              }
            />
          </VFlex>

          <VBaseInput label="Available" readOnly className="h-full!" fluid>
            <div className="text-body-xl">
              <span className="font-semibold text-black">
                {tenantUsageInfo?.usage.annotationCount}
              </span>{' '}
              <span className="text-gray-400">
                / {tenantUsageInfo?.usageLimit.annotationUsageLimit}
              </span>
            </div>
          </VBaseInput>
        </VStack>
      </VStack>
    </VModal>
  );
};

PreAnnotationModal.displayName = 'PreAnnotationModal';

export default PreAnnotationModal;
